import { useState, useEffect, useCallback } from 'react';
import { getSession, lookupEmail, seLegacyLogin } from '@/services/api';
import { useUIStore } from '@/stores/uiStore';
import type { Session, AuthLookupResult } from '@/types/tenant';

export function useAuth() {
  const { session, setSession, setLoading, selectedOrgId } = useUIStore();
  const [checking, setChecking] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      setChecking(true);
      const sessionData = await getSession();
      setSession(sessionData);
    } catch {
      setSession({ authenticated: false, tenant: null });
    } finally {
      setChecking(false);
      setLoading(false);
    }
  }, [setSession, setLoading]);

  useEffect(() => {
    checkSession();
  }, [checkSession, selectedOrgId]);

  const lookup = useCallback(async (email: string): Promise<AuthLookupResult> => {
    return lookupEmail(email);
  }, []);

  const legacyLogin = useCallback(
    async (email: string, password: string, otp?: string) => {
      const result = await seLegacyLogin(email, password, otp);
      if (result.success && result.tenant) {
        const newSession: Session = {
          authenticated: true,
          tenant: { id: result.tenant.id, name: result.tenant.name, portalUrl: '' },
        };
        setSession(newSession);
      }
      return result;
    },
    [setSession]
  );

  return {
    session,
    isAuthenticated: session?.authenticated ?? false,
    isDev: session?.role === 'dev',
    checking,
    lookup,
    legacyLogin,
    refresh: checkSession,
  };
}
